import I18n from 'I18n';
import { GET_EXTERNAL_DATA_OPTIONS, getExternalDataOptionsResults } from 'automation-ui-action-features/actions/extension/externalDataQueries';
import { getMetadataFieldsKey } from 'automation-ui-action-features/actions/extension/utils/getMetadataFieldsKey';
import useFlowId from 'automation-ui-routing/hooks/useFlowId';
import { useQuery } from 'data-fetching-client';
import { useEffect, useMemo, useState } from 'react';
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Attempt to fetch human-readable labels for the provided `value` array of
 * values by recursively fetching pages of options. Stop fetching once all
 * labels are found, the list is exhausted, or if there are any internal or
 * external API errors.
 *
 * @returns an I18n formatted list of labels for full and partial successes. If
 * a label is not found, print the original value with `(missing label)`.
 */
export const ExternalDataLabel = ({
  actionTypeId,
  dependentFields,
  fieldName,
  inputActionName,
  objectTypeId,
  value: fieldValue
}) => {
  /**
   * fetchMore does not populate the query's error property so we need to
   * handle it ourselves.
   */
  const [fetchMoreError, setFetchMoreError] = useState();
  const flowId = useFlowId() || undefined;
  const {
    data,
    error,
    fetchMore,
    loading
  } = useQuery(GET_EXTERNAL_DATA_OPTIONS, {
    variables: {
      actionTypeId,
      dependentFields,
      dependentFieldsUniqueKey: getMetadataFieldsKey(dependentFields),
      inputActionName,
      fieldName,
      flowId,
      objectTypeId
    }
  });
  const values = useMemo(() => Array.isArray(fieldValue) ? fieldValue : [fieldValue], [fieldValue]);
  useEffect(() => {
    const externalDataOptionsResults = getExternalDataOptionsResults(data);
    if (!externalDataOptionsResults) return;
    if (externalDataOptionsResults['@result'] !== 'OK') return;
    const {
      after,
      optionsByValue
    } = externalDataOptionsResults;
    const valueExists = values.every(singleValue => optionsByValue.has(singleValue));
    if (!valueExists && after) {
      fetchMore({
        variables: {
          nextPageKey: after
        }
      }).catch(setFetchMoreError);
    }
  }, [data, fetchMore, values]);

  // Loading state
  if (loading) return null;

  // Error states
  if (error) {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: I18n.text('automation-ui-action-features.action-features.extensions.external-data-label.internal-api-failure', {
        formattedValues: I18n.formatList(values)
      })
    });
  }
  const externalDataOptionsResults = getExternalDataOptionsResults(data);
  if (!externalDataOptionsResults) return null;
  if (externalDataOptionsResults['@result'] === 'ERR') {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: ["(", externalDataOptionsResults.errorType, ")"]
    });
  }

  // Partial success state
  const {
    optionsByValue
  } = externalDataOptionsResults;
  const formattedLabels = I18n.formatList(values.map(singleValue => (optionsByValue.get(singleValue) || {}).text || I18n.text('automation-ui-action-features.action-features.extensions.external-data-label.missing-label', {
    singleValue
  })));
  if (fetchMoreError) {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: I18n.text('automation-ui-action-features.action-features.extensions.external-data-label.fetch-more-failure', {
        formattedLabels
      })
    });
  }

  // Success state
  return /*#__PURE__*/_jsx(_Fragment, {
    children: formattedLabels
  });
};